import { useState, useRef, useEffect } from "react";
import { UI } from "../constants/tokens";
import { useIsMobile } from "../utils/hooks";
import { useFxRates } from "../utils/fxRates";
import { noQuoteText } from "../utils/quoteState";
import { PRICE_ONLY_LABEL, TODAY_RATE_LABEL, FX_EFFECT_LABEL } from "../utils/purchaseFx";
import { fmtPct } from "../utils/formatters";
import { companyName } from "../utils/companyName";
import { Card } from "./Card";
import LTR from "./LTR";
import Money from "./Money";
import DualMoney, { withShekel, IlsSinceSlot } from "./DualMoney";
import InfoTip from "./InfoTip";
import Icon from "./Icon";

// ─── One holding on the portfolio screen (sprint 16, stage 1) ────────────────
// Laid out the way a bank card is, in the same height the old card took:
//
//   AAPL                                         $17.71
//   Apple Inc · טכנולוגיה                  [−2.31%] −$630
//   1,500 מניות · קנייה $21.30
//   ───────────────────────────────────────────────────────
//   שווי החזקה        נתח מהתיק        שינוי מעלות ⓘ
//   $26,565.00         12.4%           −$5,385.00 −16.85%
//   ₪81,023                            −₪19,925
//
// Green and red only on what moved: the daily badge, the daily amount beside
// it, and the change from cost · always with a + or − beside the colour.
// Value and weight are levels, in full ink.
//
// The daily amount is for the whole position (price change × shares): the
// card is about what the holding did to the portfolio, and the price above it
// already says what one share did.
//
// The marking chip and the sector pill that sat on the old card: the sector
// is on the name line as text, and the marking card is in the panel the card
// opens (HoldingMarkCard), so the card itself stays three lines.

// "1,500 מניות" · fractional shares keep up to four places · "מניה אחת".
const shareCount = n => Number(n).toLocaleString("en-US", { maximumFractionDigits: 4 });
// Colour and sign are read from the number AS SHOWN, after rounding: a move
// of +0.003% prints "0.00%", and a green "0.00%" with no sign is colour
// without a direction (review).
const moveColor = v => (v > 0 ? UI.gain : v < 0 ? UI.loss : UI.text.primary);
const round2 = v => Math.round(v * 100) / 100;
// From $100,000 a figure drops its cents: "$1,234,567.89" does not fit a
// third of a phone-width card, and the cents of a six-figure sum say nothing.
const BIG = 100000;
// Sizes (26/10/2026): the price and the three figures about 30% larger than
// sprint 16's 21 and 16, so the card reads from arm's length.
// On a phone 19, and no cents from $10,000: the first column is about 105px.
const FIG_DESK = 21, FIG_PHONE = 19, LABEL = 13.5, SUB = 15;
const WHOLE_PHONE = 10000;

function LivePrice({ value, cur, size }) {
  const [cls, setCls] = useState("");
  const prev = useRef(value);
  useEffect(() => {
    if (value === prev.current) return;
    setCls(value > prev.current ? "si-price-up" : "si-price-down");
    prev.current = value;
    const t = setTimeout(() => setCls(""), 1300);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <span className={cls} style={{ display: "inline-block", padding: "0 2px" }}>
      <Money value={value} cur={cur} size={size} weight={700} color={UI.text.primary} className="si-num" style={{ lineHeight: 1.1 }}/>
    </span>
  );
}

function Cell({ label, children, info }) {
  return (
    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
      <div style={{ fontSize: LABEL, color: UI.text.secondary, fontWeight: 500, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 4 }}>
        {label}{info}
      </div>
      {children}
    </div>
  );
}

export default function HoldingCard({ s, cur, totalValue, pnl, pnlPct, pnlIls, pnlIlsSince = null, rateNote, open, onToggle }) {
  const isMobile = useIsMobile();
  const FIG = isMobile ? FIG_PHONE : FIG_DESK;
  const bigFrom = isMobile ? WHOLE_PHONE : BIG;
  const fx = useFxRates();
  const [noteOpen, setNoteOpen] = useState(false);
  // The figure as Money will print it, in the display currency: whole
  // shekels, or cents.
  const shown = v => {
    const r = fx[cur]?.rate;
    // No rate: Money prints "—", and a dash carries no direction.
    if (!r || v == null) return null;
    const x = v * r;
    return cur === "ILS" ? Math.round(x) : round2(x);
  };
  const noQuote = s.quote === "none";
  // Until the first quote lands, App carries the buy price in the current
  // price's slot. Shown as the price, that is the buy price passing for a
  // live one (review), so the figures wait instead.
  const pending = s.quote === "pending";
  const value = noQuote || pending ? null : s.currentPrice * s.shares;
  const weight = value != null && totalValue > 0 ? (value / totalValue) * 100 : null;
  // Only against a real previous close. The old card fell back to the buy
  // price, which printed the change since purchase as if it were today's.
  const hasDaily = !noQuote && !pending && s.prevClose > 0;
  const dailyPct = hasDaily ? ((s.currentPrice - s.prevClose) / s.prevClose) * 100 : null;
  const dailyAmt = hasDaily ? (s.currentPrice - s.prevClose) * s.shares : null;
  const hasBuy = s.buyPrice > 0;
  const dailyPctShown = hasDaily ? round2(dailyPct) : null;
  const dailyShown = hasDaily ? shown(dailyAmt) : null;
  const pnlNum = pending ? null : pnl;
  const pnlShown = typeof pnlNum === "number" ? shown(pnlNum) : null;
  const pctRounded = pnlPct == null || pending ? null : round2(Number(pnlPct));
  // The rate is named only when a shekel figure is on the card: in ILS mode
  // the figure itself, otherwise the line under the dollars, which is there
  // only with a live rate (DualMoney).
  const ilsShown = cur === "ILS" || !!fx.ILS?.rate;
  // Euros and pounds are the dollar gain at today's rate, said as on the
  // portfolio card (PortfolioTab, sinceNote).
  const otherCur = cur !== "USD" && cur !== "ILS";
  const note = [PRICE_ONLY_LABEL, otherCur && TODAY_RATE_LABEL, ilsShown && `${cur === "ILS" ? "" : "₪ "}${rateNote}`].filter(Boolean).join(" · ");
  const toggleKey = e => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(); }
  };
  const wait = <span className="si-num" style={{ fontSize: FIG, fontWeight: 700, color: UI.text.muted }}>…</span>;

  return (
    // The whole card opens on a click; for the keyboard and a screen reader
    // the toggle is the top section, which holds no other control (the
    // shekel line and the ⓘ below are buttons of their own · review).
    <Card onClick={onToggle} data-holding-card={s.symbol}
      style={{
        cursor: "pointer",
        borderRadius: open ? "18px 18px 0 0" : 18,
        padding: isMobile ? "14px 14px 12px" : "16px 18px 14px",
        background: UI.surface.raised,
        border: `1px solid ${UI.line.hairline}`,
      }}>
      {/* Top: who and how much on the right, the price and the day on the left */}
      <div role="button" tabIndex={0} aria-expanded={open} onKeyDown={toggleKey}
        style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, outlineOffset: 4 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 19, color: UI.text.primary, lineHeight: 1.15, display: "flex", alignItems: "center", gap: 6 }}>
            <LTR>{s.symbol}</LTR>
            {/* A line chevron in the neutral grey (sprint 17): the filled
                "▾" beside a falling figure read as one more "down". */}
            <span aria-hidden="true" style={{ display: "inline-flex" }}><Icon name={open ? "chevronUp" : "chevronDown"} size={14} color={UI.text.muted}/></span>
          </div>
          {/* The name only when it is genuinely known: it falls back to the
              symbol upstream, and the sector only when the lookup answered. */}
          {((s.name && s.name !== s.symbol) || s.sector) && (
            // The sector's source string on hover: what Finnhub calls the
            // industry, so a sector that looks wrong can be traced (4.2).
            <div data-line="name" title={s.industry ? `ענף לפי Finnhub: ${s.industry}` : undefined} style={{ fontSize: 12, color: UI.text.muted, lineHeight: 1.45, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {s.name && s.name !== s.symbol && <LTR>{companyName(s.name)}</LTR>}
              {s.name && s.name !== s.symbol && s.sector && " · "}
              {s.sector}
            </div>
          )}
          <div data-line="shares" style={{ fontSize: 12.5, color: UI.text.secondary, lineHeight: 1.45, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {Number(s.shares) === 1 ? "מניה אחת" : <><LTR className="si-num">{shareCount(s.shares)}</LTR> מניות</>}
            {hasBuy && <> · קנייה <Money value={s.buyPrice} cur={cur} size={12.5} weight={500} color={UI.text.secondary} className="si-num"/></>}
          </div>
        </div>

        <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 5 }}>
          {noQuote
            ? <span style={{ fontWeight: 600, fontSize: 13, color: UI.text.muted, lineHeight: 1.2 }}>{noQuoteText(s)}</span>
            : pending ? <span className="si-num" style={{ fontWeight: 700, fontSize: isMobile ? 25 : 27, color: UI.text.muted }}>…</span>
            : <LivePrice value={s.currentPrice} cur={cur} size={isMobile ? 25 : 27}/>}
          {hasDaily && (
            <div data-line="daily" style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
              {/* Visual order in RTL: the amount, then the badge at the edge */}
              <Money value={dailyAmt} cur={cur} size={12.5} weight={600} color={moveColor(dailyShown)} className="si-num" signed
               />
              <LTR className="si-num" title="שינוי יומי" style={{
                display: "inline-flex", alignItems: "center", fontSize: 12, fontWeight: 600,
                padding: "2px 8px", borderRadius: 999,
                background: dailyPctShown > 0 ? UI.gainSoft : dailyPctShown < 0 ? UI.lossSoft : UI.fill.subtle,
                color: moveColor(dailyPctShown),
              }}>
                {fmtPct(dailyPctShown)}
              </LTR>
            </div>
          )}
        </div>
      </div>

      {/* Bottom: three cells */}
      {/* On a phone the three share the width. On a wider screen they sit
          together at the start, in columns of a fixed width so they line up
          from card to card, instead of spreading across the card. */}
      <div data-holding-figures style={{ display: "grid", gridTemplateColumns: isMobile ? "minmax(0,1fr) minmax(0,0.62fr) minmax(0,1.38fr)" : "minmax(0,210px) minmax(0,130px) minmax(0,300px)", columnGap: isMobile ? 8 : 48, rowGap: 8, justifyContent: "start", paddingTop: 12, marginTop: 12, borderTop: `1px solid ${UI.line.hairline}` }}>
        <Cell label="שווי החזקה">
          {pending ? wait : value == null
            ? <span className="si-num" style={{ fontSize: FIG, fontWeight: 700, color: UI.text.muted }}>—</span>
            : <DualMoney usd={value} cur={cur} size={FIG}>
                <Money value={value} cur={cur} size={FIG} weight={700} color={UI.text.primary} className="si-num" whole={value >= bigFrom}/>
              </DualMoney>}
        </Cell>
        <Cell label="נתח מהתיק">
          {pending ? wait : (
            <LTR className="si-num" style={{ fontSize: FIG, fontWeight: 700, color: weight == null ? UI.text.muted : UI.text.primary, lineHeight: 1.1 }}>
              {weight == null ? "—" : `${weight.toFixed(1)}%`}
            </LTR>
          )}
        </Cell>
        <Cell label="שינוי מעלות"
          info={typeof pnlNum === "number" && <InfoTip text={note} label="על מה מחושב השינוי מעלות" size={13} open={noteOpen} onToggle={setNoteOpen}/>}>
          {pending ? wait : typeof pnlNum === "number"
            // Shekels since purchase at the purchase-day rate only: a
            // placeholder until the rate is in, "—" without one (DualMoney).
            ? <DualMoney usd={pnlNum} cur={cur} size={FIG} signed ils={pnlIls} ilsNote={rateNote} ilsSince={pnlIlsSince} ilsCaption={FX_EFFECT_LABEL}>
                <IlsSinceSlot since={cur === "ILS" ? pnlIlsSince : null} size={FIG} caption={cur === "ILS" ? FX_EFFECT_LABEL : null}>
                  <span style={{ display: "inline-flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 5, rowGap: 1, direction: "ltr" }}>
                    <Money value={pnlNum} cur={cur} size={FIG} weight={700} color={moveColor(pnlShown)} className="si-num" signed whole={Math.abs(pnlNum) >= bigFrom}/>
                    {pctRounded != null && (
                      <LTR className="si-num" style={{ fontSize: SUB, fontWeight: 600, color: moveColor(pctRounded) }}>
                        {fmtPct(pctRounded)}
                      </LTR>
                    )}
                  </span>
                </IlsSinceSlot>
              </DualMoney>
            : <span className="si-num" title={noQuote ? noQuoteText(s) : "אין מחיר קנייה"} style={{ fontSize: FIG, fontWeight: 700, color: UI.text.muted }}>—</span>}
        </Cell>
      </div>
      {noteOpen && (
        <div role="note" onClick={e => e.stopPropagation()} style={{ fontSize: 12, color: UI.text.secondary, lineHeight: 1.5, marginTop: 8 }}>
          שינוי מעלות · {withShekel(note)}
        </div>
      )}
    </Card>
  );
}
